/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function SuratMasuk() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [suratList, setSuratList] = useState([]);
  const [filteredSuratList, setFilteredSuratList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
  const [originalFileUrl, setOriginalFileUrl] = useState("");
  const [selectedSuratData, setSelectedSuratData] = useState(null);
  const [selectedSuratForEdit, setSelectedSuratForEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    nomor_surat: "",
    tanggal_surat: "",
    pengirim: "",
    perihal: "",
    file_surat: null,
  });
  const navigate = useNavigate();
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [levelJabatan, setLevelJabatan] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());
  const [selectedBulan, setSelectedBulan] = useState(null);
  const [dinasList, setDinasList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    nomor_surat: "",
    tanggal_surat: "",
    pengirim: "",
    perihal: "",
    file_surat: null,
  });

  // Generate tahun list (current year down to 2025) - sebagai object untuk konsistensi dengan Autocomplete
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push({ label: i.toString(), value: i });
  }

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  const fetchDinasList = async () => {
    try {
      const response = await axios.get(`${API_ESA_OFFICE}dinas`);
      const dinasData = response.data || [];

      setDinasList(dinasData);
    } catch (error) {
      console.error("Error fetching dinas list:", error);
      setDinasList([]);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserId(user.uid);
      setLevelJabatan(user.level_jabatan);
      setUserStatus(user.status);
    } else {
      logoutUser(navigate);
    }
    // Fetch dinas list saat component mount
    fetchDinasList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuratMasuk = async () => {
    try {
      // Tentukan endpoint berdasarkan level_jabatan dan status
      const isAdminOrLevel12 =
        userStatus === "admin" ||
        userStatus === "super admin" ||
        levelJabatan === 1 ||
        levelJabatan === 2;

      let url;
      if (isAdminOrLevel12) {
        // User dengan level_jabatan 1 atau 2 atau status admin: gunakan endpoint dinas
        url = `${API_ESA_OFFICE}surat-masuk/dinas/${dinasId}`;
      } else {
        // User biasa: gunakan endpoint user (hanya surat yang terkait)
        url = `${API_ESA_OFFICE}surat-masuk/user/${userId}`;
      }

      const params = [];
      if (selectedTahun) {
        params.push(`tahun=${selectedTahun}`);
      }
      if (selectedBulan) {
        params.push(`bulan=${selectedBulan}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await axios.get(url);
      const data = Array.isArray(response.data) ? response.data : (response.data && response.data.data) || [];
      setSuratList(data);
      setFilteredSuratList(data);
    } catch (error) {
      console.error("Error fetching surat masuk:", error);
    }
  };

  useEffect(() => {
    if (dinasId && userId && levelJabatan !== null && userStatus !== null) {
      fetchSuratMasuk();
    }
  }, [dinasId, userId, levelJabatan, userStatus, selectedTahun, selectedBulan]);

  // Filter data berdasarkan search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSuratList(suratList);
    } else {
      const filtered = suratList.filter((surat) => {
        const searchLower = searchTerm.toLowerCase();
        const nomorSurat = (surat.nomor_surat || "").toLowerCase();
        const pengirim = (surat.pengirim || "").toLowerCase();
        const perihal = (surat.perihal || "").toLowerCase();
        return (
          nomorSurat.includes(searchLower) ||
          pengirim.includes(searchLower) ||
          perihal.includes(searchLower)
        );
      });
      setFilteredSuratList(filtered);
    }
  }, [searchTerm, suratList]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTimeDownload = () => {
    return new Date().toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Export Excel - data tabel Surat Masuk
  const handleExportExcel = () => {
    if (!filteredSuratList.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }
    const downloadTime = formatDateTimeDownload();
    const tableHeaders = [
      "No",
      "No. Surat",
      "Tanggal Surat",
      "Pengirim",
      "Perihal",
      "Pengupload",
      "Pembuat Disposisi",
      "Penerima Disposisi",
      "Catatan Disposisi",
      "Status Disposisi",
      "Tanggal Disposisi",
    ];
    let tableRows = "";
    filteredSuratList.forEach((surat, index) => {
      const status = surat.sudah_didisposisi ? "Sudah Disposisi" : "Belum Disposisi";
      const tglDispo = surat.tanggal_disposisi ? formatDate(surat.tanggal_disposisi) : "-";
      const pengupload = (surat.nama_pengupload || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${(surat.nomor_surat || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>
          <td>${surat.tanggal_surat ? formatDate(surat.tanggal_surat) : "-"}</td>
          <td>${(surat.pengirim || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>
          <td>${(surat.perihal || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>
          <td>${pengupload}</td>
          <td>${(surat.nama_pembuat_disposisi || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>
          <td>${(surat.tujuan_disposisi || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>
          <td>${(surat.catatan_disposisi || "").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r\n/g, " ")}</td>
          <td>${status}</td>
          <td>${tglDispo}</td>
        </tr>
      `;
    });
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Surat Masuk - Disposisi</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Daftar Surat Masuk (Disposisi)</h2>
          <p><strong>Waktu unduh:</strong> ${downloadTime}</p>
          <p><em>Didownload dari ESA Office</em></p>
          <table>
            <thead>
              <tr>${tableHeaders.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;
    const blob = new Blob(["\ufeff" + htmlContent], { type: "application/vnd.ms-excel;charset=utf-8" });
    saveAs(blob, `surat-masuk-disposisi-${new Date().toISOString().slice(0, 10)}.xls`);
  };

  // Export PDF - data tabel Surat Masuk
  const handleExportPDF = () => {
    if (!filteredSuratList.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }
    const downloadTime = formatDateTimeDownload();
    const doc = new jsPDF("landscape", "mm", "a4");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Daftar Surat Masuk (Disposisi)", 20, 15);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Waktu unduh: " + downloadTime, 20, 22);
    doc.text("Didownload dari ESA Office", 20, 28);
    const tableHeaders = [
      "No",
      "No. Surat",
      "Tgl Surat",
      "Pengirim",
      "Perihal",
      "Pengupload",
      "Pembuat",
      "Penerima",
      "Catatan",
      "Status",
      "Tgl Dispo",
    ];
    const colWidths = [8, 28, 22, 35, 40, 28, 28, 38, 42, 20, 20];
    const startX = 20;
    const startY = 35;
    const rowHeight = 7;
    let currentY = startY;
    const drawTableHeader = () => {
      doc.setFillColor(41, 128, 185);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(255, 255, 255);
      let x = startX;
      tableHeaders.forEach((header, i) => {
        doc.rect(x, currentY, colWidths[i], rowHeight, "F");
        const text = String(header).length > 12 ? String(header).substring(0, 11) + ".." : header;
        doc.text(text, x + 1, currentY + 4.5);
        x += colWidths[i];
      });
      currentY += rowHeight;
    };
    const trunc = (str, maxLen) => {
      const s = String(str || "");
      return s.length > maxLen ? s.substring(0, maxLen - 2) + ".." : s;
    };
    const drawRow = (arr) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      let x = startX;
      const maxLens = [4, 18, 14, 22, 26, 18, 18, 24, 28, 12, 12];
      arr.forEach((cell, i) => {
        if (Math.floor((currentY - startY - rowHeight) / rowHeight) % 2 === 1) {
          doc.setFillColor(248, 249, 250);
          doc.rect(x, currentY, colWidths[i], rowHeight, "F");
        } else {
          doc.setFillColor(255, 255, 255);
          doc.rect(x, currentY, colWidths[i], rowHeight, "F");
        }
        doc.setDrawColor(200, 200, 200);
        doc.rect(x, currentY, colWidths[i], rowHeight, "S");
        doc.text(trunc(cell, maxLens[i]), x + 1, currentY + 4.5);
        x += colWidths[i];
      });
      currentY += rowHeight;
    };
    drawTableHeader();
    filteredSuratList.forEach((surat, index) => {
      if (currentY > doc.internal.pageSize.height - 25) {
        doc.addPage("landscape", "a4");
        currentY = 20;
        doc.setFontSize(8);
        doc.text("Waktu unduh: " + downloadTime + " | Didownload dari ESA Office", 20, 15);
        currentY = 22;
        drawTableHeader();
      }
      const status = surat.sudah_didisposisi ? "Sudah" : "Belum";
      const tglDispo = surat.tanggal_disposisi ? formatDate(surat.tanggal_disposisi) : "-";
      drawRow([
        index + 1,
        surat.nomor_surat || "-",
        surat.tanggal_surat ? formatDate(surat.tanggal_surat) : "-",
        surat.pengirim || "-",
        surat.perihal || "-",
        surat.nama_pengupload || "-",
        surat.nama_pembuat_disposisi || "-",
        surat.tujuan_disposisi || "-",
        (surat.catatan_disposisi || "-").replace(/\r\n/g, " "),
        status,
        tglDispo,
      ]);
    });
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Halaman " + i + " dari " + pageCount, doc.internal.pageSize.width - 35, doc.internal.pageSize.height - 10);
      doc.text("Waktu unduh: " + downloadTime + " | Didownload dari ESA Office", 20, doc.internal.pageSize.height - 10);
    }
    doc.save(`surat-masuk-disposisi-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    // Fetch dinas list saat dialog dibuka (jika belum ada atau refresh)
    if (dinasList.length === 0) {
      fetchDinasList();
    } else {
      console.log("Dinas list already loaded:", dinasList.length, "items");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      nomor_surat: "",
      tanggal_surat: "",
      pengirim: "",
      perihal: "",
      file_surat: null,
    });
  };

  const handleOpenPdfDialog = (fileUrl, suratData = null) => {
    console.log("Opening file URL:", fileUrl);
    // Simpan URL asli untuk fallback dan preview
    setOriginalFileUrl(fileUrl);
    // Simpan data surat untuk tombol disposisi
    setSelectedSuratData(suratData);
    // Set URL untuk iframe (langsung, tidak menggunakan Google Docs Viewer)
    setSelectedPdfUrl(fileUrl);
    setOpenPdfDialog(true);
  };

  const handleClosePdfDialog = () => {
    setOpenPdfDialog(false);
    setSelectedPdfUrl("");
    setOriginalFileUrl("");
    setSelectedSuratData(null);
  };

  const handleDisposisi = () => {
    if (selectedSuratData) {
      // Navigate ke halaman disposisi-baru dengan state
      navigate("/task-management/disposisi/disposisi-baru", {
        state: {
          selectedSurat: {
            surat_id: selectedSuratData.surat_id,
            nomor_surat: selectedSuratData.nomor_surat,
            perihal: selectedSuratData.perihal,
            label: `${selectedSuratData.nomor_surat} - ${selectedSuratData.perihal}`,
            value: selectedSuratData.surat_id,
          },
        },
      });
      handleClosePdfDialog();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    // Validasi tipe file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.toLowerCase().split(".").pop();

    if (
      !allowedTypes.includes(fileType) &&
      !["pdf", "jpg", "jpeg", "png"].includes(fileExtension)
    ) {
      swal({
        title: "Format File Tidak Valid!",
        text: "Hanya file PDF, JPG, dan PNG yang diizinkan.",
        icon: "error",
        button: "OK",
      });
      e.target.value = ""; // Reset input
      return;
    }

    // Validasi ukuran file (maksimal 3.5 MB)
    const maxSize = 3.5 * 1024 * 1024; // 3.5 MB dalam bytes
    if (file.size > maxSize) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: "Ukuran file maksimal adalah 3.5 MB.",
        icon: "error",
        button: "OK",
      });
      e.target.value = ""; // Reset input
      return;
    }

    setFormData((prev) => ({
      ...prev,
      file_surat: file,
    }));
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      file_surat: null,
    }));
    // Reset input file
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      const formDataToSend = new FormData();
      formDataToSend.append("nomor_surat", formData.nomor_surat);
      formDataToSend.append("tanggal_surat", formData.tanggal_surat);
      formDataToSend.append("pengirim", formData.pengirim);
      formDataToSend.append("perihal", formData.perihal);
      formDataToSend.append("dinas_id", dinasId);
      formDataToSend.append("id_pengupload", userId);
      if (formData.file_surat) {
        formDataToSend.append("file_surat", formData.file_surat);
      }

      await axios.post(`${API_ESA_OFFICE}surat-masuk`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Tutup dialog terlebih dahulu
      handleCloseDialog();

      // Refresh data surat masuk setelah berhasil menambahkan
      await fetchSuratMasuk();

      // Tampilkan notifikasi sukses setelah data berhasil di-refresh
      swal({
        title: "Berhasil!",
        text: "Surat masuk berhasil ditambahkan",
        icon: "success",
        button: "OK",
      });
    } catch (error) {
      console.error("Error adding surat masuk:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal menambahkan surat masuk",
        icon: "error",
        button: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditDialog = (surat) => {
    setSelectedSuratForEdit(surat);
    setEditFormData({
      nomor_surat: surat.nomor_surat || "",
      tanggal_surat: surat.tanggal_surat || "",
      pengirim: surat.pengirim || "",
      perihal: surat.perihal || "",
      file_surat: null,
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedSuratForEdit(null);
    setEditFormData({
      nomor_surat: "",
      tanggal_surat: "",
      pengirim: "",
      perihal: "",
      file_surat: null,
    });
    // Reset input file
    const fileInput = document.getElementById("file-upload-edit");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validasi tipe file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const fileType = file.type;
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !allowedTypes.includes(fileType) &&
      !["pdf", "jpg", "jpeg", "png"].includes(fileExtension)
    ) {
      swal({
        title: "Format File Tidak Valid!",
        text: "Hanya file PDF, JPG, dan PNG yang diizinkan.",
        icon: "error",
        button: "OK",
      });
      e.target.value = "";
      return;
    }

    // Validasi ukuran file (maksimal 3.5 MB)
    const maxSize = 3.5 * 1024 * 1024; // 3.5 MB dalam bytes
    if (file.size > maxSize) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: "Ukuran file maksimal adalah 3.5 MB.",
        icon: "error",
        button: "OK",
      });
      e.target.value = "";
      return;
    }

    setEditFormData((prev) => ({
      ...prev,
      file_surat: file,
    }));
  };

  const handleRemoveEditFile = () => {
    setEditFormData((prev) => ({
      ...prev,
      file_surat: null,
    }));
    const fileInput = document.getElementById("file-upload-edit");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmitEdit = async () => {
    if (
      !editFormData.nomor_surat ||
      !editFormData.tanggal_surat ||
      !editFormData.pengirim ||
      !editFormData.perihal
    ) {
      swal({
        title: "Data Tidak Lengkap!",
        text: "Mohon lengkapi semua field yang wajib diisi.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    try {
      setIsSaving(true);
      const formDataToSend = new FormData();
      formDataToSend.append("nomor_surat", editFormData.nomor_surat);
      formDataToSend.append("tanggal_surat", editFormData.tanggal_surat);
      formDataToSend.append("pengirim", editFormData.pengirim);
      formDataToSend.append("perihal", editFormData.perihal);
      if (editFormData.file_surat) {
        formDataToSend.append("file_surat", editFormData.file_surat);
      }

      await axios.put(
        `${API_ESA_OFFICE}surat-masuk/${selectedSuratForEdit.surat_id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Tutup dialog terlebih dahulu
      handleCloseEditDialog();

      // Refresh data surat masuk setelah berhasil update
      await fetchSuratMasuk();

      // Tampilkan notifikasi sukses setelah data berhasil di-refresh
      swal({
        title: "Berhasil!",
        text: "Surat masuk berhasil diupdate",
        icon: "success",
        button: "OK",
      });
    } catch (error) {
      console.error("Error updating surat masuk:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupdate surat masuk",
        icon: "error",
        button: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data surat masuk akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(`${API_ESA_OFFICE}surat-masuk/${id}`);

          // Refresh data surat masuk setelah berhasil menghapus
          await fetchSuratMasuk();

          // Tampilkan notifikasi sukses setelah data berhasil di-refresh
          swal({
            title: "Berhasil!",
            text: "Surat masuk berhasil dihapus",
            icon: "success",
            button: "OK",
          });
        } catch (error) {
          console.error("Error deleting surat masuk:", error);
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus surat masuk",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            akjfafjlajfdlfsa
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <MDTypography variant="h6" color="white">
                    Daftar Surat Masuk
                  </MDTypography>
                  <Button
                    variant="contained"
                    color="white"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                  >
                    Tambah Surat
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                >
                  <MDInput
                    label="Cari (Nomor, Pengirim, atau Perihal)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      flexGrow: 1,
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    options={tahunList}
                    value={
                      selectedTahun
                        ? {
                            label: selectedTahun.toString(),
                            value: selectedTahun,
                          }
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTahun(newValue.value);
                      } else {
                        setSelectedTahun(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Tahun"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 120,
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={bulanList}
                    value={
                      selectedBulan
                        ? bulanList.find((b) => b.value === selectedBulan) ||
                          null
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedBulan(newValue.value);
                      } else {
                        setSelectedBulan(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Bulan"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                  />
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={!filteredSuratList.length}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="error"
                      onClick={handleExportPDF}
                      disabled={!filteredSuratList.length}
                    >
                      <Icon>picture_as_pdf</Icon>
                      &nbsp;Export PDF
                    </MDButton>
                  </MDBox>
                </MDBox>
                {isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={2} px={2}>
                    {filteredSuratList.map((surat, index) => (
                      <Card key={surat.surat_id || index}>
                        <CardContent>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="start"
                            mb={1}
                          >
                            <MDBox flex={1}>
                              <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                mb={0.5}
                              >
                                {index + 1}. {surat.nomor_surat || "-"}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                mb={1}
                              >
                                {formatDate(surat.tanggal_surat)}
                              </MDTypography>
                            </MDBox>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(surat.surat_id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Pengirim:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {surat.pengirim || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Perihal:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {surat.perihal || "-"}
                            </MDTypography>
                          </MDBox>
                          {surat.file_surat && (
                            <MDBox mb={1}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() =>
                                  handleOpenPdfDialog(
                                    `${API_ESA_OFFICE_UPLOAD}${surat.file_surat}`,
                                    surat
                                  )
                                }
                              >
                                Lihat File
                              </Button>
                            </MDBox>
                          )}
                          <MDBox mb={1}>
                            {surat.sudah_didisposisi ? (
                              <MDBox>
                                <MDTypography variant="caption" color="success" fontWeight="medium">
                                  Sudah Dispo
                                </MDTypography>
                                {surat.tanggal_disposisi && (
                                  <MDTypography variant="caption" color="text.secondary" display="block">
                                    Tanggal: {formatDate(surat.tanggal_disposisi)}
                                  </MDTypography>
                                )}
                              </MDBox>
                            ) : (
                              (levelJabatan === 1 || levelJabatan === 2) && (
                                <Button
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  fullWidth
                                  onClick={() => {
                                    navigate("/task-management/disposisi/disposisi-baru", {
                                      state: {
                                        selectedSurat: {
                                          surat_id: surat.surat_id,
                                          nomor_surat: surat.nomor_surat,
                                          perihal: surat.perihal,
                                          label: `${surat.nomor_surat} - ${surat.perihal}`,
                                          value: surat.surat_id,
                                        },
                                      },
                                    });
                                  }}
                                >
                                  Disposisi
                                </Button>
                              )
                            )}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Pengupload:
                            </MDTypography>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={0.5}
                            >
                              {surat.foto_pengupload ? (
                                <Avatar
                                  src={surat.foto_pengupload}
                                  alt={surat.nama_pengupload || "-"}
                                  sx={{ width: 24, height: 24 }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {surat.nama_pengupload &&
                                  surat.nama_pengupload !== "-"
                                    ? surat.nama_pengupload
                                        .charAt(0)
                                        .toUpperCase()
                                    : "?"}
                                </Avatar>
                              )}
                              <MDTypography variant="body2" color="text">
                                {surat.nama_pengupload || "-"}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                              Pembuat Disposisi:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {surat.nama_pembuat_disposisi || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                              Penerima Disposisi:
                            </MDTypography>
                            <MDTypography variant="body2" color="text" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                              {surat.tujuan_disposisi || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                              Catatan Disposisi:
                            </MDTypography>
                            <MDTypography variant="body2" color="text" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {surat.catatan_disposisi || "-"}
                            </MDTypography>
                          </MDBox>
                        </CardContent>
                      </Card>
                    ))}
                    {filteredSuratList.length === 0 && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data surat masuk
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "No",
                          accessor: (row, index) => index + 1,
                          width: "5%",
                        },
                        {
                          Header: "No. Surat",
                          accessor: "nomor_surat",
                          width: "20%",
                        },
                        {
                          Header: "Tanggal Surat",
                          accessor: "tanggal_surat",
                          width: "15%",
                          Cell: ({ value }) => formatDate(value),
                        },
                        {
                          Header: "Pengirim",
                          accessor: "pengirim",
                          width: "200px",
                          Cell: ({ value }) => (
                            <MDBox
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "200px",
                                py: 1,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                {value || "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Perihal",
                          accessor: "perihal",
                          width: "300px",
                          Cell: ({ value }) => (
                            <MDBox
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "300px",
                                py: 1,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                {value || "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          id: "col_file",
                          Header: "File",
                          accessor: "file_surat",
                          width: "10%",
                          Cell: ({ value, row }) => {
                            return value ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() =>
                                  handleOpenPdfDialog(
                                    `${API_ESA_OFFICE_UPLOAD}${value}`,
                                    row.original
                                  )
                                }
                              >
                                Lihat
                              </Button>
                            ) : null;
                          },
                        },
                        {
                          id: "col_status_disposisi",
                          Header: "Status Disposisi",
                          accessor: "status_disposisi",
                          width: "15%",
                          Cell: ({ row }) => {
                            const surat = row.original;
                            if (surat.sudah_didisposisi) {
                              return (
                                <MDBox>
                                  <MDTypography
                                    variant="caption"
                                    color="success"
                                    fontWeight="medium"
                                  >
                                    Sudah Dispo
                                  </MDTypography>
                                  {surat.tanggal_disposisi && (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      {formatDate(surat.tanggal_disposisi)}
                                    </MDTypography>
                                  )}
                                </MDBox>
                              );
                            } else {
                              if (levelJabatan === 1 || levelJabatan === 2) {
                                return (
                                  <Button
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    onClick={() => {
                                      navigate(
                                        "/task-management/disposisi/disposisi-baru",
                                        {
                                          state: {
                                            selectedSurat: {
                                              surat_id: surat.surat_id,
                                              nomor_surat: surat.nomor_surat,
                                              perihal: surat.perihal,
                                              label: `${surat.nomor_surat} - ${surat.perihal}`,
                                              value: surat.surat_id,
                                            },
                                          },
                                        }
                                      );
                                    }}
                                  >
                                    Disposisi
                                  </Button>
                                );
                              } else {
                                return (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    -
                                  </MDTypography>
                                );
                              }
                            }
                          },
                        },
                        {
                          Header: "Pengupload",
                          accessor: "nama_pengupload",
                          width: "140px",
                          Cell: ({ value, row }) => (
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              {row.original.foto_pengupload ? (
                                <Avatar
                                  src={row.original.foto_pengupload}
                                  alt={value || "-"}
                                  sx={{ width: 24, height: 24 }}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ) : (
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {value && value !== "-" ? String(value).charAt(0).toUpperCase() : "?"}
                                </Avatar>
                              )}
                              <MDTypography variant="caption" color="text" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                {value != null && value !== "" ? String(value) : "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Pembuat Disposisi",
                          accessor: "nama_pembuat_disposisi",
                          width: "160px",
                          Cell: ({ value }) => (
                            <MDTypography variant="caption" color="text" sx={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                              {value != null && value !== "" ? String(value) : "-"}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "Penerima Disposisi",
                          accessor: "tujuan_disposisi",
                          width: "200px",
                          Cell: ({ value }) => (
                            <MDBox sx={{ width: "200px", minWidth: "200px", maxWidth: "200px", whiteSpace: "normal", wordBreak: "break-word", py: 0.5 }}>
                              <MDTypography variant="caption" color="text">
                                {value != null && value !== "" ? String(value) : "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Catatan Disposisi",
                          accessor: "catatan_disposisi",
                          width: "260px",
                          Cell: ({ value }) => (
                            <MDBox sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "260px", py: 0.5 }}>
                              <MDTypography variant="caption" color="text">
                                {value != null && value !== "" ? String(value).replace(/\r\n/g, "\n") : "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Aksi",
                          accessor: "aksi",
                          width: "10%",
                          Cell: ({ row }) => {
                            const surat = row.original;
                            const canEditDelete =
                              userStatus === "admin" ||
                              userStatus === "super admin" ||
                              levelJabatan === 1 ||
                              levelJabatan === 2;
                            const canDelete =
                              canEditDelete && !surat.sudah_didisposisi;

                            if (!canEditDelete) {
                              return null;
                            }

                            return (
                              <MDBox display="flex" gap={1}>
                                <IconButton
                                  color="info"
                                  size="small"
                                  onClick={() => handleOpenEditDialog(surat)}
                                  title="Edit"
                                >
                                  <EditIcon />
                                </IconButton>
                                {canDelete && (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleDelete(surat.surat_id)}
                                    title="Hapus"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </MDBox>
                            );
                          },
                        },
                      ],
                      rows: filteredSuratList,
                    }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    canSearch={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Form Tambah Surat */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tambah Surat Masuk Baru</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Nomor Surat"
                  name="nomor_surat"
                  value={formData.nomor_surat}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Tanggal Surat"
                  name="tanggal_surat"
                  type="date"
                  value={formData.tanggal_surat}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={
                    dinasList && dinasList.length > 0
                      ? dinasList
                          .map((dinas) => dinas.nama_dinas || "")
                          .filter((nama) => nama && nama.trim() !== "") // Filter out null/undefined/empty
                      : []
                  }
                  value={formData.pengirim || ""}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      pengirim: newValue || "",
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      pengirim: newInputValue || "",
                    }));
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") {
                      return option;
                    }
                    return option || "";
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <MDInput {...params} label="Pengirim" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Perihal"
                  name="perihal"
                  value={formData.perihal}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ color: "#000000" }}
                  >
                    Upload File Surat
                  </Button>
                </label>
                {formData.file_surat && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDBox flex={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          File terpilih: {formData.file_surat.name}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                        >
                          Ukuran:{" "}
                          {(formData.file_surat.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </MDTypography>
                      </MDBox>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={handleRemoveFile}
                        sx={{ ml: 1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </MDBox>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="info"
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isSaving ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Form Edit Surat */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Surat Masuk</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Nomor Surat"
                  name="nomor_surat"
                  value={editFormData.nomor_surat}
                  onChange={handleEditInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Tanggal Surat"
                  name="tanggal_surat"
                  type="date"
                  value={editFormData.tanggal_surat}
                  onChange={handleEditInputChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={
                    dinasList && dinasList.length > 0
                      ? dinasList
                          .map((dinas) => dinas.nama_dinas || "")
                          .filter((nama) => nama && nama.trim() !== "")
                      : []
                  }
                  value={editFormData.pengirim || ""}
                  onChange={(event, newValue) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      pengirim: newValue || "",
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      pengirim: newInputValue || "",
                    }));
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") {
                      return option;
                    }
                    return option || "";
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <MDInput {...params} label="Pengirim" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Perihal"
                  name="perihal"
                  value={editFormData.perihal}
                  onChange={handleEditInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={handleEditFileChange}
                  style={{ display: "none" }}
                  id="file-upload-edit"
                />
                <label htmlFor="file-upload-edit">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ color: "#000000" }}
                  >
                    Upload File Surat Baru (Opsional)
                  </Button>
                </label>
                {editFormData.file_surat && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f5f5f5",
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                      >
                        File terpilih: {editFormData.file_surat.name}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        Ukuran:{" "}
                        {(editFormData.file_surat.size / 1024 / 1024).toFixed(
                          2
                        )}{" "}
                        MB
                      </MDTypography>
                    </MDBox>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleRemoveEditFile}
                      sx={{ ml: 1 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                )}
                {selectedSuratForEdit?.file_surat &&
                  !editFormData.file_surat && (
                    <MDBox mt={1}>
                      <MDTypography variant="caption" color="text.secondary">
                        File saat ini:{" "}
                        {selectedSuratForEdit.file_surat.split("/").pop()}
                      </MDTypography>
                    </MDBox>
                  )}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isSaving}>
            Batal
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            color="info"
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isSaving ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog PDF Viewer */}
      <Dialog
        open={openPdfDialog}
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Preview File Surat</MDTypography>
            <IconButton onClick={handleClosePdfDialog} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>
          <MDBox mt={2} display="flex" justifyContent="flex-start" gap={2}>
            <Button
              variant="outlined"
              onClick={() => {
                window.open(originalFileUrl, "_blank", "noopener,noreferrer");
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Buka di Tab Baru
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const link = document.createElement("a");
                link.href = originalFileUrl;
                link.download = originalFileUrl.split("/").pop();
                link.click();
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Download
            </Button>
            {selectedSuratData && (
              <Button
                variant="outlined"
                onClick={handleDisposisi}
                sx={{
                  borderColor: "#000000",
                  color: "#000000",
                  "&:hover": {
                    borderColor: "#000000",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Disposisi
              </Button>
            )}
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedPdfUrl ? (
              <iframe
                src={selectedPdfUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading file:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat file",
                    button: "OK",
                  });
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePdfDialog}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SuratMasuk;
